import { useMemo } from "react"
import ReactMarkdown, { type Components } from "react-markdown"
import remarkGfm from "remark-gfm"
import { ChatLink } from "./chat-link"

// Markdown rendered inside a small chat bubble. react-markdown never emits raw
// HTML (no rehype-raw) and strips unsafe URL schemes, so LLM output is safe to
// render. Block elements are downsized — a real H1 in a 320px bubble is absurd,
// so every heading renders as a bold lead-in line.
//
// react-markdown 10's `Components` type clashes structurally with React 19's
// stricter element/ref types; the override props are forwarded untouched, so we
// type them loosely (no behaviour rides on the prop types here).
// biome-ignore lint/suspicious/noExplicitAny: structural clash, see above.
type Md = any

const heading = ({ node: _node, ...props }: Md) => (
	<p className="font-semibold" {...props} />
)

// Built per `ownedOrigins` so the link renderer can classify own-origin vs
// external. `ownedOrigins` is passed in by the chat container (which reads
// import.meta.env) — this module stays import.meta-free and Jest-testable.
function buildComponents(ownedOrigins: readonly string[]): Components {
	return {
		h1: heading,
		h2: heading,
		h3: heading,
		h4: heading,
		h5: heading,
		h6: heading,
		p: ({ node: _node, ...props }: Md) => <p {...props} />,
		ul: ({ node: _node, ...props }: Md) => (
			<ul className="list-disc pl-4 my-1 space-y-0.5" {...props} />
		),
		ol: ({ node: _node, ...props }: Md) => (
			<ol className="list-decimal pl-4 my-1 space-y-0.5" {...props} />
		),
		li: ({ node: _node, ...props }: Md) => <li {...props} />,
		a: ({ node: _node, href, children, ...props }: Md) =>
			typeof href === "string" ? (
				<ChatLink href={href} ownedOrigins={ownedOrigins} {...props}>
					{children}
				</ChatLink>
			) : (
				children
			),
		code: ({ node: _node, ...props }: Md) => (
			<code
				className="px-1 py-0.5 rounded bg-muted/70 font-mono text-[0.85em]"
				{...props}
			/>
		),
		// Fenced blocks keep their line shape and scroll horizontally inside the
		// bubble instead of stretching it.
		pre: ({ node: _node, ...props }: Md) => (
			<pre
				className="my-1 max-w-full overflow-x-auto rounded bg-muted/70 p-2 text-[0.85em] [&_code]:bg-transparent [&_code]:p-0"
				{...props}
			/>
		),
		strong: ({ node: _node, ...props }: Md) => (
			<strong className="font-semibold" {...props} />
		),
	}
}

export function MessageMarkdown({
	children,
	ownedOrigins,
}: {
	children: string
	ownedOrigins: readonly string[]
}) {
	const components = useMemo(
		() => buildComponents(ownedOrigins),
		[ownedOrigins],
	)
	return (
		<div className="min-w-0 space-y-1 [&_p]:leading-snug [overflow-wrap:anywhere]">
			<ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
				{children}
			</ReactMarkdown>
		</div>
	)
}
